import React, { useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import StreakModal from '@/components/StreakModal';
import LiveEvents, { PresencePill } from '@/components/LiveEvents';
import InstallPrompt from '@/components/InstallPrompt';
import BubbleLogo from '@/components/BubbleLogo';
import AlmanacListener from '@/components/AlmanacListener';
import AuroraLayer from '@/components/AuroraLayer';
import { usePresence, wsClient } from '@/lib/ws';
import {
  House, Sparkle, Users, Trophy, User, Heart, FireSimple, SignOut, Shield, Wind, ListMagnifyingGlass, ShoppingBag, Diamond, Megaphone, Crown, Star, GraduationCap, Broadcast, Package, Compass, Storefront,
} from '@phosphor-icons/react';

const NAV = [
  { to: '/home', label: 'Bubble', icon: House },
  { to: '/discover', label: 'World', icon: Compass },
  { to: '/profile', label: 'You', icon: User },
];

const AppShell = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const online = usePresence();

  // Global toast on level-up events for any of the user's bubbles
  useEffect(() => {
    if (!user) return undefined;
    return wsClient.onEvent((ev) => {
      if (ev.type === 'bubble_leveled' && ev.payload?.creator_id === user.id) {
        toast.success(`${ev.payload.bubble_name} became ${ev.payload.name} (L${ev.payload.to})`, {
          description: '+5 resonance · new unlocks available',
        });
      }
      if (ev.type === 'bubble_landed_inbox' && ev.payload?.recipient_id === user.id) {
        toast(`A bubble drifted to you · ${ev.payload.bubble_name}`, {
          description: `carrying a ${ev.payload.cargo_kind} — open it from Diary`,
          action: { label: 'Open Diary', onClick: () => navigate('/encounters') },
        });
      }
      if (ev.type === 'bubble_returned_home' && ev.payload?.owner_id === user.id) {
        toast.success(`${ev.payload.bubble_name} returned home`, {
          description: 'see what it brought back',
          action: { label: 'View', onClick: () => navigate('/encounters') },
        });
      }
    });
  }, [user, navigate]);

  return (
    <div className="min-h-screen noise relative">
      {/* Top bar */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/home" data-testid="topbar-logo" className="flex items-center gap-2">
            <BubbleLogo size="sm" tagline={true} />
            <span className="hidden sm:inline-flex ml-1"><PresencePill count={online} /></span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-bold transition-all duration-200 flex items-center gap-1.5 ${
                    isActive ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <n.icon size={16} weight="bold" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user?.founder_number && user.founder_number <= 5000 && (
              <Link to="/invites" data-testid="topbar-founder-badge"
                    className="hidden md:inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-bx-gold/40 text-bx-gold text-[10px] uppercase tracking-widest font-bold hover:bg-bx-gold/10 transition">
                <Crown size={12} weight="fill" /> #{user.founder_number}
              </Link>
            )}
            {user?.is_admin && (
              <Link to="/admin" data-testid="topbar-admin" className="p-2 rounded-full hover:bg-white/5 text-white/70">
                <Shield size={18} weight="bold" />
              </Link>
            )}
            <button
              data-testid="topbar-logout"
              onClick={() => { logout(); navigate('/'); }}
              className="p-2 rounded-full hover:bg-white/5 text-white/65"
              aria-label="logout"
            >
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        <Outlet />
      </main>
      <StreakModal />
      <LiveEvents />
      <InstallPrompt />

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-3 left-3 right-3 z-50 glass rounded-full px-2 py-2 flex items-center justify-between">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`mobnav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition ${
                isActive ? 'text-bx-cyan' : 'text-white/65'
              }`
            }
          >
            <n.icon size={20} weight={({ isActive }) => (isActive ? 'fill' : 'bold')} />
            <span className="mt-0.5">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <AlmanacListener />
      <AuroraLayer />
    </div>
  );
};

export default AppShell;
